import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ROUTES } from '@/app/routes';
import * as authService from '@/services/auth.service';
import type { OtpChallenge, ProfileUpdate } from '@/services/auth.service';
import {
  ApiError,
  clearAuthTokens,
  describeError,
  hasSession,
  loadAuthToken,
  setSessionExpiredHandler,
  setTokenRefresher,
} from '@/services/http';
import type { ManagerProfile } from '@/types';

type Result = { ok: true } | { ok: false; error: string };

interface AuthStore {
  isAuthenticated: boolean;
  manager: ManagerProfile | null;
  isPending: boolean;

  /**
   * The challenge the API is currently waiting on, or null when no sign-in is
   * in progress. Held here rather than in the login screen because a `SIGN_UP`
   * confirmation can be answered with a *second* challenge, and the screen has
   * to follow whichever one is live.
   */
  challenge: (OtpChallenge & { phone: string }) | null;

  /** Ask the API to send a code to this number. */
  requestOtp: (phone: string) => Promise<Result>;

  /**
   * Answer the outstanding challenge.
   *
   * `signedIn` is false when the API came back with *another* challenge, which
   * a new account does: Cognito confirms the sign-up, then asks for a sign-in
   * code and sends a second SMS.
   */
  verifyOtp: (code: string) => Promise<{ ok: true; signedIn: boolean } | { ok: false; error: string }>;

  /** Abandon the code screen and go back to entering a number. */
  cancelOtp: () => void;

  /** Hand the browser to the API to begin Google sign-in. */
  startGoogleSignIn: () => void;

  /** Take up the cookie session the Google callback left behind. */
  completeGoogleSignIn: () => Promise<Result>;

  /** Confirm a restored session against the API and load the profile. */
  restoreSession: () => Promise<void>;

  signOut: () => Promise<void>;

  /**
   * Drop the local session without telling the API. Used when a token ages out
   * mid-request: it is already spent, so there is nothing left to revoke.
   */
  endSession: () => void;

  /** Adopt a profile fetched elsewhere, e.g. once /managers/me resolves. */
  setManager: (manager: ManagerProfile) => void;

  /** Save the manager's own name and team name, and adopt what the API returns. */
  updateProfile: (update: ProfileUpdate) => Promise<Result>;
}

// Any session from a previous visit is available before the first request.
loadAuthToken();

// Player tokens refresh against /auth/refresh; the admin console has its own.
setTokenRefresher(authService.refreshPlayerSession);

/**
 * The app always starts signed out unless a session was restored.
 *
 * This used to skip the login screen entirely without an API — there was
 * nothing to authenticate against, so the demo signed itself in. That made a
 * shared link open straight onto someone's team, which is the wrong first
 * impression and no gate at all. A demo sign-in still issues a token, so the
 * same check works with or without a backend.
 */
const startsAuthenticated = (): boolean => hasSession();

/**
 * What to tell a player for each of the API's sign-in failure codes.
 *
 * The API sends these in the envelope's `error` field with player-safe text
 * and keeps Cognito's own message in its logs. The wording is ours rather than
 * the server's so each one says what to do next.
 */
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  INVALID_PHONE: 'Check the phone number and try again.',
  CODE_MISMATCH: 'That code is incorrect. Check the SMS and try again.',
  CODE_EXPIRED: 'That code has expired. Tap "Resend code" for a new one.',
  TOO_MANY_ATTEMPTS: 'Too many attempts. Wait a few minutes and try again.',
  OTP_UNAVAILABLE: "We couldn't send a code right now. Please try again shortly.",
};

/**
 * The message to show for a failed sign-in step.
 *
 * Known codes map to their message. Anything else — a code this build does
 * not know, a validation error, a server fault — gets `fallback`, with the
 * original logged so it can still be diagnosed. Two exceptions keep their own
 * text: the server being unreachable, and the demo build's rejected code,
 * which has no API behind it to send a code.
 */
function describeAuthError(cause: unknown, fallback: string): string {
  if (cause instanceof ApiError) {
    const known = cause.code ? AUTH_ERROR_MESSAGES[cause.code] : undefined;
    if (known) return known;
    if (cause.status === 0 || (cause.status === 401 && !cause.code)) return describeError(cause);
  }

  console.error('[auth]', cause);
  return fallback;
}

const OTP_SEND_FAILED = "We couldn't send a code right now. Please try again shortly.";
const OTP_CHECK_FAILED = "We couldn't check your code right now. Please try again shortly.";

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      isAuthenticated: startsAuthenticated(),
      manager: null,
      isPending: false,
      challenge: null,

      requestOtp: async (phone) => {
        const normalised = authService.toE164(phone);

        if (!authService.isE164(normalised)) {
          return { ok: false, error: 'Enter a valid mobile number, e.g. 0712 345 678.' };
        }

        set({ isPending: true });

        try {
          const challenge = await authService.requestMobileOtp(normalised);
          set({ challenge: { ...challenge, phone: normalised }, isPending: false });
          return { ok: true };
        } catch (cause) {
          set({ isPending: false });
          return { ok: false, error: describeAuthError(cause, OTP_SEND_FAILED) };
        }
      },

      verifyOtp: async (code) => {
        const pending = get().challenge;
        if (!pending) return { ok: false, error: 'That code has expired. Start again.' };

        set({ isPending: true });

        try {
          const result = await authService.verifyMobileOtp({
            phone: pending.phone,
            code,
            challenge: pending.challenge,
            ...(pending.session ? { session: pending.session } : {}),
          });

          // Confirming a new account can hand back another challenge — Cognito
          // sends a second SMS — so this is not necessarily the way in yet.
          if (result.status === 'challenge') {
            set({
              challenge: { ...result, phone: pending.phone },
              isPending: false,
            });
            return { ok: true, signedIn: false };
          }

          set({
            isAuthenticated: true,
            manager: result.manager,
            challenge: null,
            isPending: false,
          });
          return { ok: true, signedIn: true };
        } catch (cause) {
          set({ isPending: false });
          return { ok: false, error: describeAuthError(cause, OTP_CHECK_FAILED) };
        }
      },

      cancelOtp: () => set({ challenge: null }),

      startGoogleSignIn: () => {
        const returnTo = `${window.location.origin}${ROUTES.authGoogle}`;
        window.location.assign(authService.googleSignInUrl(returnTo));
      },

      completeGoogleSignIn: async () => {
        set({ isPending: true });

        try {
          const manager = await authService.adoptGoogleSession();
          set({ isAuthenticated: true, manager, challenge: null, isPending: false });
          return { ok: true };
        } catch (cause) {
          set({ isAuthenticated: false, manager: null, isPending: false });
          return { ok: false, error: describeError(cause) };
        }
      },

      restoreSession: async () => {
        if (!hasSession()) return;

        try {
          const manager = await authService.getCurrentManager();
          set({ isAuthenticated: true, manager });
        } catch {
          // The API has disowned the session. Anything else — an unreachable
          // server — already fell back to a fixture rather than throwing.
          get().endSession();
        }
      },

      endSession: () => {
        clearAuthTokens();
        set({ isAuthenticated: false, manager: null, challenge: null });
      },

      signOut: async () => {
        await authService.signOut();
        set({ isAuthenticated: false, manager: null, challenge: null });
      },

      setManager: (manager) => set({ manager }),

      updateProfile: async (update) => {
        const current = get().manager;
        if (!current) return { ok: false, error: 'Sign in again to edit your profile.' };

        try {
          const manager = await authService.updateProfile(current, update);
          set({ manager });
          return { ok: true };
        } catch (cause) {
          return { ok: false, error: describeError(cause) };
        }
      },
    }),
    {
      name: 'matchday-auth',
      version: 5,

      /**
       * Only the profile is persisted, and only so a returning visitor sees
       * their name before `/auth/me` answers.
       *
       * `isAuthenticated` is deliberately *not* stored. It was, and the two
       * could drift: the flag lives here while the token lives in its own key,
       * so clearing the token — private browsing, evicted storage, a manual
       * clear — left a store that believed it was signed in. The app would
       * render the whole shell and then fail every request. Deriving the flag
       * from the token on every load makes that state unrepresentable.
       *
       * An in-flight challenge is not persisted either: a reload mid sign-in
       * should start over rather than resume with a spent session id.
       */
      partialize: (s) => ({ manager: s.manager }),

      // A profile without a session is nobody, so it is dropped rather than
      // shown to whoever opens the tab next.
      onRehydrateStorage: () => (state) => {
        if (state && !hasSession()) state.manager = null;
      },

      migrate: () => ({ manager: null }),
    },
  ),
);

/**
 * An expired session is discovered by the transport, mid-request. Without this
 * the store would still believe it was signed in and every screen would fail
 * one by one instead of returning to the login page.
 */
setSessionExpiredHandler(() => {
  useAuthStore.getState().endSession();
});
